import React, { useState } from 'react';
import { 
  Card, 
  Timeline, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  Collapse, 
  Empty,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  TruckOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
  GlobalOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Panel } = Collapse;

const ShippingTimeline = ({ order }) => {
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [showRawData, setShowRawData] = useState(false);

  // Get tracking info from the first order item that has tracking data
  const trackingInfo = order.orderItems?.find(item => item.trackingInfo?.scrapeData)?.trackingInfo;
  console.log('Tracking Info:', order);
  if (!trackingInfo?.scrapeData?.events) {
    return (
      <Card title="Shipping Timeline" style={{ marginBottom: 24 }}>
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No tracking information available"
        />
      </Card>
    );
  }

  const events = trackingInfo.scrapeData.events || [];
  const displayedEvents = showAllEvents ? events : events.slice(0, 5);

  const getEventIcon = (status, detail) => {
    const statusLower = status?.toLowerCase() || '';
    const detailLower = detail?.toLowerCase() || '';
    
    if (detailLower.includes('delivered')) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (detailLower.includes('out for delivery')) {
      return <CarOutlined style={{ color: '#1890ff' }} />;
    }
    if (detailLower.includes('in transit') || detailLower.includes('departed')) {
      return <TruckOutlined style={{ color: '#1890ff' }} />;
    }
    if (detailLower.includes('arrived')) {
      return <EnvironmentOutlined style={{ color: '#722ed1' }} />;
    }
    if (detailLower.includes('exception') || detailLower.includes('failed')) {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    if (detailLower.includes('international')) {
      return <GlobalOutlined style={{ color: '#13c2c2' }} />;
    }
    return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
  };

  const getEventColor = (status, detail) => {
    const detailLower = detail?.toLowerCase() || '';
    
    if (detailLower.includes('delivered')) return '#52c41a';
    if (detailLower.includes('out for delivery')) return '#1890ff';
    if (detailLower.includes('exception') || detailLower.includes('failed')) return '#ff4d4f';
    if (detailLower.includes('international')) return '#13c2c2';
    if (detailLower.includes('transit') || detailLower.includes('arrived')) return '#722ed1';
    return '#d9d9d9';
  };

  const formatEventDate = (dateString) => {
    try {
      const date = dayjs(dateString);
      const now = dayjs();
      const diffDays = now.diff(date, 'day');
      
      if (diffDays === 0) {
        return `Today, ${date.format('HH:mm')}`;
      } else if (diffDays === 1) {
        return `Yesterday, ${date.format('HH:mm')}`;
      } else if (diffDays < 7) {
        return `${diffDays} days ago, ${date.format('HH:mm')}`;
      } else {
        return date.format('MMM DD, YYYY HH:mm');
      }
    } catch (error) {
      return dateString;
    }
  };

  const trackingNumber = order.orderItems?.find(item => item.trackingNumber)?.trackingNumber;
  const carrier = order.orderItems?.find(item => item.carrier)?.carrier;
  const lastMileCarrier = trackingInfo.lastMileCarrier;
  const lastMileTracking = trackingInfo.lastMileTracking;

  return (
    <Card 
      title={
        <Space>
          <TruckOutlined />
          <span>Shipping Timeline</span>
          {trackingInfo.scrapeData.status && (
            <Tag color={getEventColor('', trackingInfo.scrapeData.status)}>
              {trackingInfo.scrapeData.status}
            </Tag>
          )}
        </Space>
      }
      extra={
        <Space>
          {events.length > 5 && (
            <Button 
              size="small" 
              type="link"
              icon={showAllEvents ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setShowAllEvents(!showAllEvents)}
            >
              {showAllEvents ? 'Show Less' : `Show All (${events.length})`}
            </Button>
          )}
          <Button 
            size="small" 
            type="link"
            onClick={() => setShowRawData(!showRawData)}
          >
            Raw Data
          </Button>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      {/* Tracking Info Header */}
      <Row gutter={[16, 8]} style={{ marginBottom: 16, padding: '12px', backgroundColor: '#ffffff', border: '1px solid #e8e8e8', borderRadius: 6 }}>
        <Col xs={24} sm={12} md={6}>
          <Text type="secondary" style={{ fontSize: '12px' }}>Primary Tracking</Text>
          <div>
            <Text strong style={{ fontFamily: 'monospace', fontSize: '13px' }}>
              {trackingNumber || 'N/A'}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {carrier || 'Unknown Carrier'}
            </Text>
          </div>
        </Col>
        
        {lastMileCarrier && lastMileTracking && (
          <Col xs={24} sm={12} md={6}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Last Mile</Text>
            <div>
              <Text strong style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                {lastMileTracking}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {lastMileCarrier}
              </Text>
            </div>
          </Col>
        )}
        
        <Col xs={24} sm={12} md={6}>
          <Text type="secondary" style={{ fontSize: '12px' }}>Route</Text>
          <div>
            <Text style={{ fontSize: '13px' }}>
              {trackingInfo.scrapeData.rawData?.route || 'N/A'}
            </Text>
          </div>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Text type="secondary" style={{ fontSize: '12px' }}>Last Scraped</Text>
          <div>
            <Text style={{ fontSize: '11px' }}>
              {trackingInfo.scrapedAt ? dayjs(trackingInfo.scrapedAt).format('MMM DD, HH:mm') : 'N/A'}
            </Text>
          </div>
        </Col>
      </Row>

      {/* Timeline */}
      <Timeline mode="left" style={{ marginTop: 16 }}>
        {displayedEvents.map((event, index) => (
          <Timeline.Item
            key={index}
            dot={getEventIcon(event.status, event.detail)}
            color={getEventColor(event.status, event.detail)}
          >
            <div style={{ marginLeft: 8 }}>
              <div style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: '14px' }}>
                  {event.detail || event.status}
                </Text>
              </div>
              
              <Row>
                <Col span={12}>
                  <Space size={4} align="center">
                    <ClockCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {formatEventDate(event.date)}
                    </Text>
                  </Space>
                </Col>
                
                {event.location && (
                  <Col span={12}>
                    <Space size={4} align="center">
                      <EnvironmentOutlined style={{ fontSize: '12px', color: '#999' }} />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {event.location}
                      </Text>
                    </Space>
                  </Col>
                )}
              </Row>
            </div>
          </Timeline.Item>
        ))}
      </Timeline>

      {/* Raw Data Collapse */}
      {showRawData && (
        <Collapse size="small" style={{ marginTop: 16 }}>
          <Panel header="Raw Tracking Data" key="rawData">
            <pre style={{ 
              fontSize: '11px', 
              backgroundColor: '#ffffff', 
              border: '1px solid #e8e8e8',
              padding: '12px', 
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(trackingInfo.scrapeData, null, 2)}
            </pre>
          </Panel>
        </Collapse>
      )}
    </Card>
  );
};

export default ShippingTimeline;
